/**
 * AiEstimationWizard
 * ==================
 *
 * Step-by-step wizard rendered by the public form page when the parent
 * form has `form_type === 'ai_estimation'`. Walks the visitor through
 * the preset's ordered steps and submits the structured payload to
 * `POST /api/public/form/{slug}/submit`.
 *
 * Props:
 *   preset:        { steps: [...], result_label, success_message, ... }
 *   workspace:     { branding: {...}, name } (for theming/header — optional)
 *   themeBtnColor: string — primary button colour resolved by the parent
 *   onSubmit:      async ({ answers, photos, tracking, contact }) => response
 *
 * Stable contract: each step.key is persisted untouched in
 * `Lead.ai_estimation_answers` so future calculators / scoring engines
 * can read them without any UI knowledge.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle, Upload, X, Camera, AlertTriangle, Phone } from 'lucide-react';

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_PHOTO_EDGE = 1600;
const ALLOWED_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', ''];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // data URL (server strips header)
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Downscale + re-encode to JPEG so phone photos (5–15 Mo) become light
// payloads; also normalises HEIC on browsers able to decode it.
async function compressPhoto(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode'));
      el.src = objectUrl;
    });
    const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    if (!dataUrl.startsWith('data:image/jpeg')) throw new Error('encode');
    return { dataUrl, size: Math.round((dataUrl.length - 23) * 0.75) };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function AiEstimationWizard({ preset, themeBtnColor = '#F59E0B', onSubmit, onTrack, workspacePhone }) {
  const steps = useMemo(() => preset?.steps || [], [preset]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // --- Silent funnel tracking (fire-and-forget, never blocks the wizard) ---
  const startTrackedRef = useRef(false);
  useEffect(() => {
    if (startTrackedRef.current || typeof onTrack !== 'function') return;
    startTrackedRef.current = true;
    try { onTrack('wizard_start'); } catch { /* tracking must never break the form */ }
  }, [onTrack]);
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]); // [{ name, dataUrl, size }]
  const [contact, setContact] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [stepError, setStepError] = useState(null);

  const totalSteps = steps.length;

  // --- Conditional steps (show_if) ---------------------------------------
  // Steps without `show_if` (all of pave_uni) are always visible, so the
  // legacy wizard behaviour is strictly unchanged for existing presets.
  const stepMatchesConditions = (s, ans) => {
    let conds = s.show_if;
    if (!conds) return true;
    if (!Array.isArray(conds)) conds = [conds];
    return conds.every((c) => {
      const val = ans[c.field];
      const allowed = c.in || [];
      if (Array.isArray(val)) return val.some((v) => allowed.includes(v));
      return allowed.includes(val);
    });
  };
  const visibleSteps = useMemo(
    () => steps.filter((s) => stepMatchesConditions(s, answers)),
    [steps, answers]
  );
  const visibleTotal = visibleSteps.length;
  const step = visibleSteps[Math.min(currentIndex, Math.max(visibleTotal - 1, 0))] || null;
  const progress = visibleTotal > 0 ? Math.round(((Math.min(currentIndex, visibleTotal - 1) + 1) / visibleTotal) * 100) : 0;

  // Clamp index if a changed answer shrank the visible path.
  useEffect(() => {
    if (currentIndex > visibleTotal - 1 && visibleTotal > 0) {
      setCurrentIndex(visibleTotal - 1);
    }
  }, [visibleTotal, currentIndex]);

  // --- Urgent option detection (preset-driven, e.g. active water) --------
  const urgentInfo = useMemo(() => {
    for (const s of steps) {
      for (const opt of s.options || []) {
        if (!opt.urgent) continue;
        const val = answers[s.key];
        const hit = Array.isArray(val) ? val.includes(opt.value) : val === opt.value;
        if (hit) {
          return {
            title: opt.urgent_title || preset?.urgent_alert?.title || 'Intervention urgente recommandée',
            message: opt.urgent_message || preset?.urgent_alert?.message || '',
          };
        }
      }
    }
    return null;
  }, [answers, steps, preset]);

  const setSingle = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setStepError(null);
  };

  const toggleMulti = (key, value) => {
    setAnswers((prev) => {
      const arr = Array.isArray(prev[key]) ? [...prev[key]] : [];
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(value);
      return { ...prev, [key]: arr };
    });
    setStepError(null);
  };

  const [photoBusy, setPhotoBusy] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  const handlePhotos = async (files) => {
    setStepError(null);
    setPhotoBusy(true);
    const room = MAX_PHOTOS - photos.length;
    const list = Array.from(files);
    const accepted = [];
    const rejected = [];
    for (const file of list.slice(0, room)) {
      if (!ALLOWED_MIMES.includes((file.type || '').toLowerCase())) {
        rejected.push(`${file.name} : format non pris en charge (JPG, PNG, WEBP)`);
        continue;
      }
      try {
        const { dataUrl, size } = await compressPhoto(file);
        accepted.push({ name: file.name, dataUrl, size });
      } catch {
        // Canvas could not decode (ex. HEIC on Android) — send original if it is a supported type and light enough.
        const rawOk = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes((file.type || '').toLowerCase());
        if (rawOk && file.size <= MAX_PHOTO_BYTES) {
          try {
            accepted.push({ name: file.name, dataUrl: await fileToBase64(file), size: file.size });
            continue;
          } catch { /* fall through */ }
        }
        rejected.push(`${file.name} : impossible de lire cette image (convertissez-la en JPG)`);
      }
    }
    if (list.length > room) {
      rejected.push(`Maximum ${MAX_PHOTOS} photos — ${list.length - room} photo(s) ignorée(s)`);
    }
    setPhotos((prev) => [...prev, ...accepted].slice(0, MAX_PHOTOS));
    if (rejected.length > 0) setStepError(rejected.join(' · '));
    setPhotoBusy(false);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const validateCurrent = () => {
    if (!step) return true;
    const key = step.key;
    if (step.type === 'contact') {
      // P-ADDR — Canadian postal code regex (A1A 1A1 / A1A1A1). Same rule
      // as the server side guard in `_submit_ai_estimation_form`; we double
      // up here to give an instant inline error instead of a 422 on submit.
      const CA_POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
      for (const fld of step.fields || []) {
        const val = (contact[fld.key] || '').toString().trim();
        if (fld.required && !val) {
          setStepError(`Le champ « ${fld.label} » est requis.`);
          return false;
        }
        if (fld.input_type === 'email' && val) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
            setStepError('Adresse courriel invalide.');
            return false;
          }
        }
        if (fld.input_type === 'postal_code' && val) {
          if (!CA_POSTAL_RE.test(val)) {
            setStepError('Code postal canadien invalide (ex. : H2X 1Y4).');
            return false;
          }
        }
      }
      return true;
    }
    if (!step.required) return true;
    const val = answers[key];
    if (step.type === 'multi_choice') {
      if (!Array.isArray(val) || val.length === 0) {
        setStepError('Veuillez choisir au moins une option.');
        return false;
      }
      return true;
    }
    if (step.type === 'single_choice') {
      if (!val) { setStepError('Veuillez choisir une option pour continuer.'); return false; }
      return true;
    }
    if (step.type === 'long_text') {
      if (!val || !val.toString().trim()) {
        setStepError('Veuillez compléter ce champ.');
        return false;
      }
      return true;
    }
    if (step.type === 'number') {
      if (!val || !val.toString().trim()) {
        setStepError('Veuillez indiquer une valeur.');
        return false;
      }
      const num = parseFloat(val.toString().replace(',', '.'));
      if (Number.isNaN(num) || num <= 0) {
        setStepError('Veuillez entrer un nombre positif.');
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateCurrent()) return;
    if (typeof onTrack === 'function') {
      try { onTrack('step_completed', { step_index: currentIndex, step_key: step?.key }); } catch { /* silent */ }
    }
    setCurrentIndex((i) => Math.min(i + 1, visibleTotal - 1));
  };

  const goPrev = () => {
    setStepError(null);
    setCurrentIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateCurrent()) return;
    setSubmitting(true);
    try {
      const payload = {
        answers: { ...answers, contact: { ...contact } },
        photos: photos.map((p) => p.dataUrl),
        honeypot,
      };
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  if (!step) {
    return (
      <Card className="form-card">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Ce formulaire n&apos;a pas d&apos;étapes configurées.</p>
        </CardContent>
      </Card>
    );
  }

  const isLast = currentIndex === visibleTotal - 1;

  return (
    <Card className="shadow-xl form-card" data-testid="ai-estimation-wizard">
      <CardHeader className="border-b">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-muted-foreground">
              Étape {currentIndex + 1} / {visibleTotal}
            </span>
            <span className="font-mono text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
          <CardTitle className="text-xl pt-2" data-testid="wizard-step-title">{step.label}</CardTitle>
          {step.hint && (
            <p className="text-sm text-muted-foreground">{step.hint}</p>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-5">
        {/* ============== Urgent alert (active water infiltration) ============== */}
        {urgentInfo && (
          <div
            className="rounded-lg border-2 border-red-500 bg-red-50 p-4 space-y-3"
            data-testid="wizard-urgent-alert"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-700">{urgentInfo.title}</p>
                {urgentInfo.message && (
                  <p className="text-sm text-red-700/90 mt-1">{urgentInfo.message}</p>
                )}
              </div>
            </div>
            {workspacePhone && (
              <a
                href={`tel:${String(workspacePhone).replace(/[^+\d]/g, '')}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm transition-colors"
                data-testid="wizard-urgent-call-btn"
              >
                <Phone className="h-4 w-4" />
                Appeler maintenant — {workspacePhone}
              </a>
            )}
            <p className="text-xs text-red-700/80">
              Vous pouvez aussi continuer le formulaire pour nous transmettre vos photos et coordonnées.
            </p>
          </div>
        )}

        {/* ============== single_choice ============== */}
        {step.type === 'single_choice' && (
          <div className="grid grid-cols-1 gap-2" data-testid={`wizard-options-${step.key}`}>
            {(step.options || []).map((opt) => {
              const selected = answers[step.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSingle(step.key, opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    selected
                      ? 'border-2 shadow-sm font-semibold'
                      : 'border hover:border-foreground/30'
                  }`}
                  style={selected ? { borderColor: themeBtnColor, backgroundColor: `${themeBtnColor}15` } : undefined}
                  data-testid={`wizard-option-${step.key}-${opt.value}`}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex items-center justify-center h-5 w-5 rounded-full border-2 shrink-0 ${
                        selected ? '' : 'border-muted-foreground/40'
                      }`}
                      style={selected ? { borderColor: themeBtnColor, backgroundColor: themeBtnColor } : undefined}
                    >
                      {selected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                    </span>
                    <span className="flex flex-col">
                      <span>{opt.label}</span>
                      {opt.help && (
                        <span className="text-xs text-muted-foreground mt-0.5">{opt.help}</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ============== multi_choice ============== */}
        {step.type === 'multi_choice' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`wizard-options-${step.key}`}>
            {(step.options || []).map((opt) => {
              const selected = Array.isArray(answers[step.key]) && answers[step.key].includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                    selected ? 'border-2 font-semibold' : 'border hover:border-foreground/30'
                  }`}
                  style={selected ? { borderColor: themeBtnColor, backgroundColor: `${themeBtnColor}15` } : undefined}
                  data-testid={`wizard-option-${step.key}-${opt.value}`}
                >
                  <Checkbox
                    checked={selected}
                    onCheckedChange={() => toggleMulti(step.key, opt.value)}
                  />
                  <span className="text-sm flex flex-col">
                    <span>{opt.label}</span>
                    {opt.help && (
                      <span className="text-xs text-muted-foreground mt-0.5">{opt.help}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {/* ============== number ============== */}
        {step.type === 'number' && (
          <Input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={answers[step.key] || ''}
            onChange={(e) => setSingle(step.key, e.target.value)}
            placeholder={step.placeholder || 'Ex. : 120'}
            className="h-12 max-w-xs"
            data-testid={`wizard-input-${step.key}`}
          />
        )}

        {/* ============== long_text ============== */}
        {step.type === 'long_text' && (
          <Textarea
            value={answers[step.key] || ''}
            onChange={(e) => setSingle(step.key, e.target.value)}
            placeholder={step.placeholder || ''}
            className="min-h-[140px]"
            data-testid={`wizard-input-${step.key}`}
          />
        )}

        {/* ============== photo_upload ============== */}
        {step.type === 'photo_upload' && (
          <div className="space-y-3" data-testid={`wizard-photos-${step.key}`}>
            <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
              <Camera className="h-8 w-8 text-muted-foreground" />
              <span className="font-medium">Ajouter des photos</span>
              <span className="text-xs text-muted-foreground">
                JPG, PNG ou WEBP — max {MAX_PHOTOS} photos (compressées automatiquement)
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={photoBusy}
                onChange={(e) => { handlePhotos(e.target.files); e.target.value = ''; }}
                data-testid="wizard-photo-input"
              />
            </label>
            {photoBusy && (
              <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="wizard-photo-busy">
                <Loader2 className="h-3 w-3 animate-spin" /> Préparation des photos…
              </p>
            )}

            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="wizard-photo-previews">
                {photos.map((p, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                    <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-black"
                      onClick={() => removePhoto(idx)}
                      aria-label="Retirer la photo"
                      data-testid={`wizard-photo-remove-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{photos.length} / {MAX_PHOTOS} photo{photos.length > 1 ? 's' : ''}</span>
              {photos.length === 0 && <span className="italic">Optionnel — vous pouvez passer cette étape.</span>}
            </div>
          </div>
        )}

        {/* ============== contact ============== */}
        {step.type === 'contact' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              name="slx_hp_field"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              data-testid="wizard-honeypot"
            />
            {(step.fields || []).map((fld) => (
              <div key={fld.key} className={`space-y-1.5 ${fld.key === 'full_name' || fld.key === 'address' || fld.key === 'project_notes' ? 'sm:col-span-2' : ''}`}>
                <Label htmlFor={`contact-${fld.key}`} className="flex items-center gap-1">
                  {fld.label}
                  {fld.required && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={`contact-${fld.key}`}
                  type={fld.input_type || 'text'}
                  value={contact[fld.key] || ''}
                  onChange={(e) => { setContact((prev) => ({ ...prev, [fld.key]: e.target.value })); setStepError(null); }}
                  className="h-12"
                  data-testid={`wizard-contact-${fld.key}`}
                />
              </div>
            ))}
          </div>
        )}

        {stepError && (
          <p className="text-sm text-destructive" data-testid="wizard-step-error">{stepError}</p>
        )}

        {/* ============== Navigation ============== */}
        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={goPrev}
            disabled={currentIndex === 0 || submitting}
            data-testid="wizard-prev-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>

          {!isLast ? (
            <Button
              type="button"
              onClick={goNext}
              className="font-bold"
              style={{ backgroundColor: themeBtnColor }}
              disabled={submitting}
              data-testid="wizard-next-btn"
            >
              Continuer
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              className="font-bold"
              style={{ backgroundColor: themeBtnColor }}
              disabled={submitting}
              data-testid="wizard-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Recevoir mon estimation préliminaire
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
